import { useState, useEffect, useCallback, useMemo } from 'react';
import { Box, Button, CircularProgress, Paper } from '@mui/material';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { Search } from '@mui/icons-material';
import { SCUIBreadCrumb, SCUICustomDataGrid } from '../../atomic/molecules';
import { SCUIContent } from '../../atomic/organisms';
import { PageUrl } from '../../types/enums/pageUrl-enums';
import { SCUIButton, SCUILoadingLinear, SCUINoRecordComponent } from '../../atomic/atoms';
import { SearchDrawer } from './components';
import airdropShipment from '../../assets/icons/svg/pageIcons/airdropShipment.svg';
import CargoAddressBlockingQuery from '../../services/query/cargoAddressBlocking/CargoAddressBlockingQuery.services';
import CargoAddressBlockingCommand from '../../services/command/cargoAddressBlocking/CargoAddressBlockingCommand.services';
import PostCheckoutQuery from '../../services/query/postCheckout/PostCheckoutQuery.service';
import CargoManagementQuery from '../../services/query/cargoManagement/CargoManagementQuery.services';
import TimeHelper from '../../utils/time';
import { downloadExcel } from '../../utils/excelFileHelper';
import { CAB, cabCls } from './cargoAddressBlocking.classes';

// Helper function to get countryId from localStorage
const getCountryId = (): number => {
    try {
        const countryId = JSON.parse(localStorage.getItem('selectedCountry') || '{}')?.countryId;
        return countryId ? Number(countryId) : 1;
    } catch {
        return 1;
    }
};

// Constants
const DEFAULT_PAGE_SIZE = 10;
const EXCEL_FOLDER_NAME = 'CargoAddressBlocking';

interface Option {
    id: number;
    name: string;
}

interface GridRow {
    id: number;
    shippingCompanyId: number;
    shippingCompanyName: string;
    cityId: number;
    cityName: string;
    countyId: number;
    countyName: string;
    neighborhoodName: string;
    addressEntityId: number;
    changedOn: string;
    changedBy: string;
}

export const CargoAddressBlocking = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [isSearched, setIsSearched] = useState(false);
    const [searchDrawerOpen, setSearchDrawerOpen] = useState(false);
    const [addDrawerOpen, setAddDrawerOpen] = useState(false);
    const [gridData, setGridData] = useState<GridRow[]>([]);
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
    const [pageNumber, setPageNumber] = useState(0);
    const [cargoCompanyOptions, setCargoCompanyOptions] = useState<Option[]>([]);
    const [cityOptions, setCityOptions] = useState<Option[]>([]);
    const [selectedRowIds, setSelectedRowIds] = useState<number[]>([]);

    const { t } = useTranslation();

    // Fetch cargo companies on mount (filter by isCargoCompany === true)
    useEffect(() => {
        PostCheckoutQuery.GetShippingCompanies()
            .then((res: any) => {
                if (res?.shippingCompanies && Array.isArray(res.shippingCompanies)) {
                    const options = res.shippingCompanies
                        .filter(
                            (company: any) =>
                                company?.shippingCompanyId != null &&
                                company?.name != null &&
                                company?.isCargoCompany === true
                        )
                        .map((company: any) => ({
                            id: company.shippingCompanyId,
                            name: company.name ?? '',
                        }));
                    setCargoCompanyOptions(options);
                }
            })
            .catch((err: any) => {
                console.error('Error fetching cargo companies:', err);
            });

        // Fetch cities (addressEntityTypeId = 3)
        const params = {
            countryId: getCountryId(),
            cultureCode: localStorage.getItem('i18nextLng') || 'tr-TR',
            parentAddressEntityId: 0,
            deliveryMethodId: 0,
            addressEntityTypeId: 3, // City type
            isVisible: true,
            isMpStore: true,
        };

        CargoManagementQuery.SubAddressEntities(params)
            .then((res: any) => {
                const addressEntities = res?.data?.addressEntities || res?.addressEntities || [];
                if (Array.isArray(addressEntities)) {
                    const options = addressEntities.map((item: any) => ({
                        id: item?.addressEntityId ?? item?.id,
                        name: item?.name ?? '',
                    }));
                    setCityOptions(options);
                }
            })
            .catch((err: any) => {
                console.error('Error fetching cities:', err);
            });
    }, []);

    const handleSearch = useCallback(
        (filterParams: any) => {
            setIsLoading(true);
            setSearchDrawerOpen(false);

            const payload = {
                shippingCompanyId: filterParams.cargoCompanyId,
                addressEntityIds: filterParams.countyIds,
                isDeleted: false,
                parentAddressEntityIds: filterParams.parentAddressEntityIds,
            };

            CargoAddressBlockingQuery.GetBlockedAddresses(payload)
                .then((res: any) => {
                    // Transform response to grid format
                    const items = res?.blockedAddresses || [];
                    const transformedData: GridRow[] = Array.isArray(items)
                        ? items.map((item: any, index: number) => ({
                              id: item.id || index,
                              shippingCompanyId: item.shippingCompanyId || 0,
                              shippingCompanyName: item.shippingCompanyName || '-',
                              cityId: item.cityId || 0,
                              cityName: item.cityName || '-',
                              countyId: item.countyId || 0,
                              countyName: item.countyName || '-',
                              neighborhoodName: item.addressEntityName || '-',
                              addressEntityId: item.addressEntityId || 0,
                              changedOn: item.changedOn || '',
                              changedBy: item.changedBy || '-',
                          }))
                        : [];

                    setGridData(transformedData);
                    setSelectedRowIds([]);
                    setIsSearched(true);
                })
                .catch((err: any) => {
                    console.error('Error fetching blocked addresses:', err);
                    Swal.fire({
                        icon: 'error',
                        title: t('general.error'),
                        text: t('cargoAddressBlocking.searchError'),
                        confirmButtonText: t('general.ok'),
                    });
                })
                .finally(() => {
                    setIsLoading(false);
                });
        },
        [t]
    );

    const handleDelete = useCallback(
        async (row: GridRow) => {
            // Show confirmation dialog
            const result = await Swal.fire({
                title: t('general.sureText'),
                text: t('cargoAddressBlocking.deleteConfirm'),
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#d33',
                cancelButtonColor: '#3085d6',
                confirmButtonText: t('general.delete'),
                cancelButtonText: t('general.cancel'),
            });

            if (result.isConfirmed) {
                // Store previous data for rollback
                const previousData = [...gridData];

                // Optimistic UI: Remove the row immediately
                setGridData((prev) => prev.filter((item) => item.id !== row.id));

                const payload = {
                    shippingCompanyId: row.shippingCompanyId,
                    addressEntityIds: [row.addressEntityId],
                };

                CargoAddressBlockingCommand.DeleteBlockedAddress(payload)
                    .then(() => {
                        Swal.fire({
                            icon: 'success',
                            title: t('general.success'),
                            text: t('cargoAddressBlocking.deleteSuccess'),
                            confirmButtonText: t('general.ok'),
                        });
                    })
                    .catch((err: any) => {
                        console.error('Error deleting blocked address:', err);
                        // Rollback: Put the row back if error
                        setGridData(previousData);
                        Swal.fire({
                            icon: 'error',
                            title: t('general.error'),
                            text: t('cargoAddressBlocking.deleteError'),
                            confirmButtonText: t('general.ok'),
                        });
                    });
            }
        },
        [t, gridData]
    );

    const handleAddNew = useCallback(() => {
        setAddDrawerOpen(true);
    }, []);

    const handleAddSuccess = useCallback(
        (filterParams: any) => {
            // After successful add, search with the same params used for adding
            if (filterParams) {
                handleSearch(filterParams);
            }
        },
        [handleSearch]
    );

    const handleSelectionModelChange = useCallback((selectionModel: any) => {
        const normalizedSelection = Array.isArray(selectionModel) ? selectionModel.map(Number) : [];
        setSelectedRowIds(normalizedSelection);
    }, []);

    const handleBulkDelete = useCallback(async () => {
        if (selectedRowIds.length === 0) return;

        const result = await Swal.fire({
            title: t('general.sureText'),
            text: `${selectedRowIds.length} adet kayıt toplu olarak silinecek. Devam etmek istiyor musunuz?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: t('general.delete'),
            cancelButtonText: t('general.cancel'),
        });

        if (!result.isConfirmed) return;

        const previousData = [...gridData];
        const idsToDelete = [...selectedRowIds];
        const selectedRows = gridData.filter((item) => idsToDelete.includes(item.id));
        const deleteGroups = selectedRows.reduce(
            (acc, row) => {
                const key = row.shippingCompanyId;
                if (!acc[key]) {
                    acc[key] = {
                        shippingCompanyId: row.shippingCompanyId,
                        addressEntityIds: [],
                    };
                }
                acc[key].addressEntityIds.push(row.addressEntityId);
                return acc;
            },
            {} as Record<number, { shippingCompanyId: number; addressEntityIds: number[] }>
        );

        const bulkPayloads = Object.values(deleteGroups).map((group) => ({
            shippingCompanyId: group.shippingCompanyId,
            addressEntityIds: Array.from(new Set(group.addressEntityIds)),
        }));

        setGridData((prev) => prev.filter((item) => !idsToDelete.includes(item.id)));
        setSelectedRowIds([]);

        Promise.all(bulkPayloads.map((payload) => CargoAddressBlockingCommand.DeleteBlockedAddress(payload)))
            .then(() => {
                Swal.fire({
                    icon: 'success',
                    title: t('general.success'),
                    text: t('cargoAddressBlocking.deleteSuccess'),
                    timer: 1500,
                });
            })
            .catch(() => {
                setGridData(previousData);
                Swal.fire({
                    icon: 'error',
                    title: t('general.error'),
                    text: t('general.errorOccured'),
                    confirmButtonText: t('general.ok'),
                });
            });
    }, [selectedRowIds, t, gridData]);

    const handleExportExcel = useCallback(() => {
        const excelRows = gridData.map((row) => ({
            cargoCompanyName: row.shippingCompanyName || '-',
            cityName: row.cityName || '-',
            countyName: row.countyName || '-',
            neighborhood: row.neighborhoodName || '-',
            updatedDate: row.changedOn ? TimeHelper.formatDate(row.changedOn) : '-',
            updatedBy: row.changedBy || '-',
        }));

        const now = new Date();
        const dd = String(now.getDate()).padStart(2, '0');
        const mm = String(now.getMonth() + 1).padStart(2, '0');
        const yyyy = now.getFullYear();
        const fileName = `KargoAdresBlocklama_${dd}.${mm}.${yyyy}.xlsx`;

        downloadExcel({
            excelDownloadData: excelRows,
            excelDownloadFolderName: EXCEL_FOLDER_NAME,
            fileNameOverride: fileName,
        });
    }, [gridData]);

    const openSearchDrawer = useCallback(() => {
        setSearchDrawerOpen(true);
    }, []);

    const closeSearchDrawer = useCallback(() => {
        setSearchDrawerOpen(false);
    }, []);

    const closeAddDrawer = useCallback(() => {
        setAddDrawerOpen(false);
    }, []);

    const resetSearch = useCallback(() => {
        setIsSearched(false);
        setGridData([]);
        setSelectedRowIds([]);
    }, []);

    // Memoize delete button styles to prevent recreation
    const deleteButtonSx = useMemo(
        () => ({
            minWidth: '30px !important',
            width: '30px !important',
            minHeight: '30px !important',
            height: '30px !important',
            backgroundColor: isLoading ? '#ccc' : 'red',
            borderRadius: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: 'none',
            p: 0,
            m: 0,
            '&:hover': {
                backgroundColor: isLoading ? '#ccc' : '#c00',
                boxShadow: 'none',
            },
        }),
        [isLoading]
    );

    const columnData = useMemo(
        () => [
            {
                field: 'shippingCompanyName',
                headerName: t('cargoAddressBlocking.columns.cargoCompanyName'),
                flex: 1,
                minWidth: 150,
                renderCell: (params: any) => <span>{params?.row?.shippingCompanyName ?? '-'}</span>,
            },
            {
                field: 'cityName',
                headerName: t('cargoAddressBlocking.columns.city'),
                flex: 1,
                minWidth: 120,
                renderCell: (params: any) => <span>{params?.row?.cityName ?? '-'}</span>,
            },
            {
                field: 'countyName',
                headerName: t('cargoAddressBlocking.columns.county'),
                flex: 1,
                minWidth: 120,
                renderCell: (params: any) => <span>{params?.row?.countyName ?? '-'}</span>,
            },
            {
                field: 'neighborhoodName',
                headerName: t('cargoAddressBlocking.columns.neighborhood'),
                flex: 1,
                minWidth: 140,
                renderCell: (params: any) => <span>{params?.row?.neighborhoodName ?? '-'}</span>,
            },
            {
                field: 'changedOn',
                headerName: t('cargoAddressBlocking.columns.updatedDate'),
                width: 160,
                align: 'center',
                headerAlign: 'center',
                renderCell: (params: any) => {
                    const dateValue = params?.row?.changedOn;
                    if (dateValue && dateValue !== '-' && dateValue !== '') {
                        return <span>{TimeHelper.formatDate(dateValue)}</span>;
                    }
                    return <span>-</span>;
                },
            },
            {
                field: 'changedBy',
                headerName: t('cargoAddressBlocking.columns.updatedBy'),
                flex: 1,
                minWidth: 150,
                renderCell: (params: any) => <span>{params?.row?.changedBy ?? '-'}</span>,
            },
            {
                field: 'actions',
                headerName: t('cargoAddressBlocking.columns.delete'),
                width: 100,
                align: 'center',
                headerAlign: 'center',
                sortable: false,
                renderCell: (params: any) => (
                    <Button
                        onClick={() => handleDelete(params.row)}
                        disabled={isLoading}
                        sx={deleteButtonSx}
                        className={cabCls(CAB.input, CAB.inputButton, CAB.btnDeleteRow)}
                    >
                        <DeleteIcon sx={{ color: 'white', fontSize: 14 }} />
                    </Button>
                ),
            },
        ],
        [t, isLoading, handleDelete, deleteButtonSx]
    );

    return (
        <SCUIContent className={`${CAB.page} `}>
            <Paper
                component="div"
                className={CAB.paper}
                sx={{
                    p: '24px',
                    paddingTop: '36px',
                    display: 'flex',
                    flexDirection: 'column',
                    width: '100%',
                }}
            >
                <SCUIBreadCrumb
                    title={t('cargoAddressBlocking.title')}
                    componentLink={PageUrl.cargoAddressBlocking.url}
                    onClick={resetSearch}
                />
                <SCUILoadingLinear display={isLoading} />

                {/* Search Drawer */}
                <SearchDrawer
                    isDrawerOpen={searchDrawerOpen}
                    handleDrawerClose={closeSearchDrawer}
                    handleSearch={handleSearch}
                    mode="search"
                    cargoCompanyOptions={cargoCompanyOptions}
                    cityOptions={cityOptions}
                    isLoading={isLoading}
                />

                {/* Add Drawer */}
                <SearchDrawer
                    isDrawerOpen={addDrawerOpen}
                    handleDrawerClose={closeAddDrawer}
                    onAddSuccess={handleAddSuccess}
                    mode="add"
                    cargoCompanyOptions={cargoCompanyOptions}
                    cityOptions={cityOptions}
                    setIsLoading={setIsLoading}
                    isLoading={isLoading}
                />

                {isSearched ? (
                    <Box sx={{ marginTop: '24px' }}>
                        {/* Action Buttons */}
                        <Box
                            className={CAB.toolbar}
                            sx={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginBottom: '12px' }}
                        >
                            <SCUIButton
                                text={t('general.addNew')}
                                type="primary"
                                sizeType="fixed"
                                style={{ height: '35px' }}
                                size={'m'}
                                buttonType="button"
                                onClick={handleAddNew}
                                showStartIcon
                                startIcon={<AddIcon sx={{ color: '#fff' }} />}
                                disabled={isLoading}
                                className={cabCls(CAB.input, CAB.inputButton, CAB.btnAddNew)}
                            />
                            <SCUIButton
                                text={t('general.exportToExcel')}
                                type="secondary"
                                sizeType="fixed"
                                style={{ height: '35px' }}
                                size={'m'}
                                buttonType="button"
                                onClick={handleExportExcel}
                                showStartIcon
                                startIcon={<FileDownloadIcon sx={{ color: '#0d6efd' }} />}
                                disabled={isLoading}
                                className={cabCls(CAB.input, CAB.inputButton, CAB.btnExportExcel)}
                            />
                            <SCUIButton
                                text={t('general.search')}
                                type="secondary"
                                sizeType="fixed"
                                style={{ height: '35px' }}
                                size={'m'}
                                buttonType="button"
                                onClick={openSearchDrawer}
                                showStartIcon
                                startIcon={<Search sx={{ color: '#0d6efd' }} />}
                                disabled={isLoading}
                                className={cabCls(CAB.input, CAB.inputButton, CAB.btnOpenSearch)}
                            />
                        </Box>
                        <Box
                            className={CAB.bulkActions}
                            sx={{
                                display: 'flex',
                                justifyContent: 'flex-end',
                                gap: '8px',
                                marginBottom: '4px',
                                marginTop: '24px',
                            }}
                        >
                            {selectedRowIds?.length > 0 && (
                                <Button
                                    variant="contained"
                                    color="error"
                                    onClick={handleBulkDelete}
                                    startIcon={<DeleteIcon />}
                                    disabled={isLoading}
                                    sx={{ padding: '8px 16px !important' }}
                                    className={cabCls(CAB.input, CAB.inputButton, CAB.btnBulkDelete)}
                                >
                                    {`${t('cargoAddressBlocking.bulkDelete')} (${selectedRowIds?.length})`}
                                </Button>
                            )}
                        </Box>
                        <Box className={CAB.datagrid}>
                            <SCUICustomDataGrid
                                checkboxSelection
                                selectionOnClick={true}
                                onSelectionModelChange={handleSelectionModelChange}
                                selectionModel={selectedRowIds}
                                onPageSizeChange={setPageSize}
                                pageSize={pageSize}
                                onPageChange={setPageNumber}
                                getRowId={(row: any) => row?.id}
                                page={pageNumber}
                                rows={gridData}
                                columns={columnData}
                                slotProps={{
                                    baseCheckbox: {
                                        className: cabCls(CAB.input, CAB.inputCheckbox, CAB.cbGridSelection),
                                    },
                                }}
                            />
                        </Box>
                    </Box>
                ) : (
                    <Box
                        className={CAB.emptyState}
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <SCUINoRecordComponent img={airdropShipment} text={t('cargoAddressBlocking.search')} />
                        <SCUIButton
                            text={t('general.search')}
                            type="primary"
                            sizeType="fixed"
                            size={'m'}
                            showStartIcon
                            style={{ width: '120px', marginTop: '16px' }}
                            disabled={isLoading}
                            startIcon={
                                isLoading ? (
                                    <CircularProgress size={20} style={{ color: 'white' }} />
                                ) : (
                                    <Search style={{ color: 'white' }} />
                                )
                            }
                            onClick={openSearchDrawer}
                            className={cabCls(CAB.input, CAB.inputButton, CAB.btnEmptySearch)}
                        />
                    </Box>
                )}
            </Paper>
        </SCUIContent>
    );
};
